import elearningApi from 'api/elearningApi';
import { GENDERS } from 'constants/driving-student.constant';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Image, Modal, Row } from 'react-bootstrap';
import styled, { keyframes } from 'styled-components';
// moodleApi.getToken removed in Phase 2D step 2 — change-password now goes
// through the BE proxy (`elearningApi.changeMyPassword`).
import 'react-circular-progressbar/dist/styles.css';
import { MdEdit, MdLockOutline } from 'react-icons/md';
import { toastWrapper } from 'utils';
import { formatPhoneNumber } from 'utils/commonUtils';
import { formatDobDisplay } from 'utils/dobUtils';
import { useDispatch, useSelector } from 'react-redux';
import { selectElearningUser, updateElearningData } from 'store/elearning.slice';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  PasswordInput,
  PrimaryButton,
  Spinner,
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';

const MOBILE = '@media (max-width: 768px)';
const MIN_PASSWORD = 8;

function ElearningStudentMyPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userName =
    JSON.parse(localStorage.getItem('moodleSiteInfo') || '{}')?.username || null;
  const student = useSelector(selectElearningUser);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });
  // Retry 1 lần khi signed URL hết hạn (1h) hoặc lỗi load khác.
  const [avatarRetried, setAvatarRetried] = useState(false);
  // Track image load để fade-in real avatar lên trên skeleton. Reset
  // false khi src đổi (vd portrait được upload, BE trả URL mới).
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const refreshStudent = useCallback(async () => {
    // Phase 2D step 5: prefer sessionToken-only bootstrap. axiosClient
    // already attaches Authorization: Bearer <sessionToken>; BE looks
    // up Driving via the JWT subject so moodleToken is optional.
    try {
      const res = await elearningApi.getMyBootstrap();
      if (res?.data) {
        dispatch(updateElearningData({ elearningUser: res.data }));
      }
    } catch (_) { /* best-effort */ }
  }, [dispatch]);

  useEffect(() => {
    setAvatarRetried(false);
    setAvatarLoaded(false);
  }, [student?.portraitUrl, student?.elearningAvatarUrl]);

  const pwValidation = useMemo(() => {
    const oldEmpty = oldPassword.length === 0;
    const newTooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD;
    const newEmpty = newPassword.length === 0;
    const confirmEmpty = confirmPassword.length === 0;
    const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
    return {
      oldEmpty, newTooShort, newEmpty, confirmEmpty, mismatch,
      canSubmit: !oldEmpty && !newEmpty && !confirmEmpty && !newTooShort && !mismatch,
    };
  }, [oldPassword, newPassword, confirmPassword]);

  const handleUpdatePassword = async () => {
    setPwError('');
    setTouched({ newPassword: true, confirmPassword: true });
    if (pwValidation.oldEmpty) {
      setPwError('Mật khẩu cũ không được để trống.');
      return;
    }
    if (pwValidation.newEmpty || pwValidation.newTooShort) {
      setPwError(`Mật khẩu mới phải có ít nhất ${MIN_PASSWORD} ký tự.`);
      return;
    }
    if (pwValidation.mismatch || pwValidation.confirmEmpty) {
      setPwError('Mật khẩu mới và xác nhận không khớp.');
      return;
    }

    setPwSaving(true);
    // Phase 2D step 2: backend verifies oldPassword by trading it for a
    // fresh wstoken server-side, then rotates the encrypted token on
    // Driving. The browser never holds a Moodle credential during this
    // flow — no client-side moodleApi.getToken needed.
    try {
      await elearningApi.changeMyPassword({
        username: userName,
        oldPassword,
        newPassword,
      });
      toastWrapper('Cập nhật mật khẩu thành công!', 'success');
      resetPasswordForm();
      setShowPasswordModal(false);
    } catch (err) {
      const code = err?.response?.data?.data?.code;
      const msg = code === 'invalid_old_password'
        ? 'Mật khẩu cũ không chính xác.'
        : (err?.response?.data?.message || err?.message || 'Cập nhật mật khẩu thất bại.');
      setPwError(msg);
    } finally {
      setPwSaving(false);
    }
  };

  const resetPasswordForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwError('');
    setTouched({ newPassword: false, confirmPassword: false });
  };

  if (!student) {
    return (
      <PageWrap>
        <Container className='py-5'>
          <Spinner block label='Đang tải thông tin...' />
        </Container>
      </PageWrap>
    );
  }

  const avatarSrc =
    student?.elearningAvatarUrl ||
    student?.portraitUrl ||
    'common/profile.jpg';

  return (
    <PageWrap>
      <Container className='py-3 py-md-4'>
        <PageTitle>Thông tin cá nhân</PageTitle>

        <ProfileCard>
          <ProfileLayout>
            {/* Avatar + actions */}
            <AvatarCol>
              <AvatarWrap>
                <AvatarImage
                  src={avatarSrc}
                  alt='avatar'
                  $loaded={avatarLoaded}
                  onLoad={() => setAvatarLoaded(true)}
                  onError={(e) => {
                    if (!avatarRetried && (student?.portraitUrl || student?.elearningAvatarUrl)) {
                      setAvatarRetried(true);
                      refreshStudent();
                    } else if (e?.target && e.target.src.indexOf('common/profile.jpg') === -1) {
                      e.target.src = 'common/profile.jpg';
                    } else {
                      // Cả retry + fallback đều fail → vẫn hiện skeleton.
                      // Set loaded=true để tránh shimmer chạy vô hạn nhưng
                      // không render image broken.
                      setAvatarLoaded(true);
                    }
                  }}
                />
              </AvatarWrap>
              <AvatarActions>
                <PrimaryButton
                  size='sm'
                  variant='secondary'
                  onClick={() => navigate(PATH.ELEARNING.STUDENT.INFO_CONFIRMATION)}
                >
                  <MdEdit /> Cập nhật thông tin
                </PrimaryButton>
                <PrimaryButton
                  size='sm'
                  variant='secondary'
                  onClick={() => setShowPasswordModal(true)}
                >
                  <MdLockOutline /> Đổi mật khẩu
                </PrimaryButton>
              </AvatarActions>
            </AvatarCol>

            {/* Info — personal */}
            <InfoCol>
              <PersonName>{student?.name || 'Chưa cập nhật'}</PersonName>
              <InfoGrid>
                <InfoItem label='Mã học viên' value={student?.registrationCode} />
                <InfoItem label='Số CMND/CCCD' value={student?.cardNumber} />
                <InfoItem label='Giới tính' value={GENDERS[student?.gender?.toUpperCase?.()] || student?.gender} />
                <InfoItem label='Ngày sinh' value={formatDobDisplay(student?.dob)} />
                <InfoItem label='Số điện thoại' value={formatPhoneNumber(student?.tel)} />
                <InfoItem label='Địa chỉ thường trú' value={student?.residence} wide />
              </InfoGrid>
            </InfoCol>

            {/* Course */}
            <CourseCol>
              <CourseTitle>Khoá học</CourseTitle>
              <InfoItem label='Tên khoá' value={student?.course?.name} />
              <InfoItem label='Mã khoá' value={student?.course?.code} />
              <InfoItem label='Hạng bằng' value={student?.drivingType?.label} />
              <InfoItem
                label='Khai giảng'
                value={student?.course?.enrollmentDate
                  ? new Date(student.course.enrollmentDate).toLocaleDateString('en-GB')
                  : null}
              />
              <InfoItem
                label='Bế giảng'
                value={student?.course?.graduationDate
                  ? new Date(student.course.graduationDate).toLocaleDateString('en-GB')
                  : null}
              />
            </CourseCol>
          </ProfileLayout>
        </ProfileCard>

        <NavActions>
          <PrimaryButton
            variant='secondary'
            onClick={() => navigate(PATH.ELEARNING.STUDENT.COURSE)}
          >
            Danh sách môn học
          </PrimaryButton>
          <PrimaryButton onClick={() => navigate(PATH.ELEARNING.STUDENT.RESULT)}>
            Kết quả học tập
          </PrimaryButton>
        </NavActions>
      </Container>

      {/* Password modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => { setShowPasswordModal(false); resetPasswordForm(); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: typography.size.lg, fontWeight: typography.weight.bold }}>
            Đổi mật khẩu
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pwError && <Alert variant='danger'>{pwError}</Alert>}
          <PasswordInput
            label='Mật khẩu cũ'
            required
            autoComplete='current-password'
            placeholder='Nhập mật khẩu hiện tại'
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            disabled={pwSaving}
          />
          <PasswordInput
            label='Mật khẩu mới'
            required
            placeholder={`Tối thiểu ${MIN_PASSWORD} ký tự`}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
            error={touched.newPassword && pwValidation.newTooShort
              ? `Mật khẩu phải có ít nhất ${MIN_PASSWORD} ký tự.`
              : ''}
            disabled={pwSaving}
          />
          <PasswordInput
            label='Xác nhận mật khẩu mới'
            required
            placeholder='Nhập lại mật khẩu mới'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
            error={touched.confirmPassword && pwValidation.mismatch
              ? 'Mật khẩu không khớp.'
              : ''}
            disabled={pwSaving}
          />
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton
            variant='secondary'
            onClick={() => { setShowPasswordModal(false); resetPasswordForm(); }}
            disabled={pwSaving}
          >
            Đóng
          </PrimaryButton>
          <PrimaryButton
            onClick={handleUpdatePassword}
            loading={pwSaving}
            loadingText='Đang cập nhật...'
            disabled={!pwValidation.canSubmit}
          >
            Cập nhật mật khẩu
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </PageWrap>
  );
}

// Helper component
function InfoItem({ label, value, wide }) {
  return (
    <InfoItemWrap $wide={wide}>
      <InfoLabel>{label}</InfoLabel>
      <InfoValue>{value || <InfoMuted>Chưa cập nhật</InfoMuted>}</InfoValue>
    </InfoItemWrap>
  );
}

/* ─── styled ─── */

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
`;

const PageTitle = styled.h2`
  font-size: ${typography.size.h2};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[5]};

  ${MOBILE} {
    font-size: ${typography.size.h3};
    margin-bottom: ${spacing[4]};
  }
`;

const ProfileCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  padding: ${spacing[5]};
  margin-bottom: ${spacing[5]};

  ${MOBILE} {
    padding: ${spacing[4]};
  }
`;

const ProfileLayout = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 1fr;
  gap: ${spacing[6]};

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  ${MOBILE} {
    grid-template-columns: 1fr;
    gap: ${spacing[4]};
  }
`;

const AvatarCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[4]};

  @media (max-width: 992px) {
    grid-column: 1 / -1;
  }
`;

const avatarShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  /* Skeleton background hiển thị trong khi <img> chưa load. Khi
     onLoad fire, AvatarImage opacity = 1 → đè skeleton. Khi src đổi,
     opacity về 0 + skeleton lộ lại → mượt khi đổi avatar. */
  background: linear-gradient(
    90deg,
    ${colors.surfaceMuted} 0%,
    ${colors.gray200 || '#e2e8f0'} 50%,
    ${colors.surfaceMuted} 100%
  );
  background-size: 200% 100%;
  animation: ${avatarShimmer} 1.4s ease-in-out infinite;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  border: 3px solid ${colors.surface};
  box-shadow: ${shadow.md};
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 250ms ease-in-out;
`;

const AvatarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  max-width: 200px;
`;

const InfoCol = styled.div`
  ${MOBILE} {
    grid-column: 1 / -1;
  }
`;

const CourseCol = styled.div`
  ${MOBILE} {
    grid-column: 1 / -1;
  }
`;

const PersonName = styled.h4`
  font-size: ${typography.size.xl};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[4]};
`;

const CourseTitle = styled.h4`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray700};
  margin-bottom: ${spacing[3]};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[3]} ${spacing[4]};

  ${MOBILE} {
    grid-template-columns: 1fr;
    gap: ${spacing[3]};
  }
`;

const InfoItemWrap = styled.div`
  ${(p) => p.$wide && 'grid-column: 1 / -1;'}
  margin-bottom: ${spacing[2]};
`;

const InfoLabel = styled.div`
  font-size: ${typography.size.xs};
  color: ${colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${spacing[1]};
`;

const InfoValue = styled.div`
  font-size: ${typography.size.base};
  color: ${colors.gray900};
  word-break: break-word;
`;

const InfoMuted = styled.span`
  color: ${colors.gray400};
  font-style: italic;
`;

const NavActions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing[3]};

  ${MOBILE} {
    flex-direction: column;
    > * { width: 100%; }
  }
`;

export default ElearningStudentMyPage;
